import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { getMe, getMyPermissions, login as apiLogin, signup as apiSignup, logout as apiLogout } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = loading
  const [ready, setReady] = useState(false);
  const [sections, setSections] = useState(null); // null = not yet loaded

  useEffect(() => {
    (async () => {
      // The auth token lives in an httpOnly cookie; ask the server who we are.
      try {
        setUser(await getMe());
      } catch {
        setUser(false); // not authenticated (expected when no/expired session)
      } finally {
        setReady(true);
      }
    })();
    // Run once on mount; getMe/setUser/setReady are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the current user's allowed sections (staff only) for the sidebar & route guards.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (user && user.role && user.role !== "client") {
        try {
          const r = await getMyPermissions();
          if (alive) setSections(Array.isArray(r?.sections) ? r.sections : []);
        } catch {
          if (alive) setSections([]);
        }
      } else {
        setSections(null);
      }
    })();
    return () => { alive = false; };
  }, [user]);

  const signIn = useCallback(async (email, password) => {
    const data = await apiLogin(email, password); // sets httpOnly cookie
    setUser(data.user);
    return data.user;
    // apiLogin (import) and setUser (setter) are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signUp = useCallback(async (payload) => {
    const data = await apiSignup(payload); // creates org + admin (email unverified)
    if (data?.user) setUser(data.user); // only when auto-login is returned (not with email verification)
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = useCallback(async () => {
    try {
      await apiLogout();
    } catch (e) {
      console.error("auth: logout request failed (clearing session anyway)", e);
    }
    setUser(false);
    window.location.href = "/login";
    // apiLogout (import) and setUser (setter) are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const can = useCallback((...roles) => user && roles.includes(user.role), [user]);

  // Section-level access. Admin has everything; dashboard is always available to staff.
  const canAccess = useCallback((key) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (key === "dashboard") return true;
    if (key === "guide") return true;
    return Array.isArray(sections) && sections.includes(key);
  }, [user, sections]);

  // Permissions are "ready" once loaded for staff (clients don't need them).
  const permsReady = !user || user.role === "client" ? true : sections !== null;

  const value = useMemo(
    () => ({ user, ready, signIn, signUp, signOut, can, canAccess, sections, permsReady }),
    [user, ready, signIn, signUp, signOut, can, canAccess, sections, permsReady]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
